/**
 * Load More button for collection pages.
 * Fetches the next page via Section Rendering API and appends products to the grid.
 *
 * @customElement load-more-button
 */
class LoadMoreButton extends HTMLElement {
  #loading = false;
  #abortController = null;

  connectedCallback() {
    this.button = this.querySelector('[data-load-more]');
    this.spinner = this.querySelector('[data-load-more-spinner]');
    this.label = this.querySelector('[data-load-more-label]');

    if (!this.button) return;

    this.button.addEventListener('click', this.#handleClick);
    this.#updateVisibility();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#handleClick);
    this.#abortController?.abort();
  }

  /** @returns {string} Section ID from closest results-list */
  get sectionId() {
    const resultsList = this.closest('results-list');
    return resultsList?.getAttribute('section-id') ?? '';
  }

  /** @returns {HTMLUListElement|null} The product grid */
  get grid() {
    const resultsList = this.closest('results-list');
    return resultsList?.querySelector('[ref="grid"]') ?? null;
  }

  /** @returns {number} Current highest page loaded in the grid */
  get currentMaxPage() {
    const grid = this.grid;
    if (!grid) return 1;

    const items = grid.querySelectorAll('[data-page]');
    let max = 1;
    items.forEach((item) => {
      const p = parseInt(item.getAttribute('data-page'), 10);
      if (p > max) max = p;
    });
    return max;
  }

  /** @returns {number} Total pages available */
  get totalPages() {
    const grid = this.grid;
    return parseInt(grid?.getAttribute('data-last-page') ?? '1', 10);
  }

  /** Hide button when all pages are loaded */
  #updateVisibility() {
    if (!this.button) return;

    const nextPage = this.currentMaxPage + 1;
    if (nextPage > this.totalPages) {
      this.style.display = 'none';
    } else {
      this.style.display = '';
    }
  }

  /** Set loading state */
  #setLoading(loading) {
    this.#loading = loading;
    if (this.button) {
      this.button.setAttribute('aria-busy', loading.toString());
      this.button.disabled = loading;
    }
    if (this.spinner) {
      this.spinner.style.display = loading ? '' : 'none';
    }
    if (this.label) {
      this.label.textContent = loading
        ? (this.getAttribute('data-loading-text') || 'Cargando...')
        : (this.getAttribute('data-label-text') || 'Cargar más productos');
    }
  }

  /**
   * Fetch next page and append products to grid
   */
  #handleClick = async () => {
    if (this.#loading) return;

    const grid = this.grid;
    if (!grid) return;

    const nextPage = this.currentMaxPage + 1;
    if (nextPage > this.totalPages) {
      this.#updateVisibility();
      return;
    }

    this.#setLoading(true);

    try {
      this.#abortController = new AbortController();

      // Build Section Rendering API URL
      const url = new URL(window.location.href);
      url.searchParams.set('page', nextPage.toString());
      url.searchParams.set('section_id', this.sectionId);
      url.searchParams.sort();

      const response = await fetch(url.toString(), {
        signal: this.#abortController.signal,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();

      // Parse response and extract product items
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('[ref="grid"]');

      if (newGrid) {
        const newItems = newGrid.querySelectorAll(':scope > [ref="cards[]"]');
        grid.append(...newItems);
      }

      // Update URL without reload
      const pageUrl = new URL(window.location.href);
      pageUrl.searchParams.set('page', nextPage.toString());
      history.pushState({}, '', pageUrl.toString());

      this.#updateVisibility();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[LoadMore]', error);
      }
    } finally {
      this.#setLoading(false);
      this.#abortController = null;
    }
  };
}

if (!customElements.get('load-more-button')) {
  customElements.define('load-more-button', LoadMoreButton);
}
